import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { StickyContainer } from 'react-sticky';
import { FILTER_POSITION_SETTINGS } from '../../constants';

export type StickySidebarContextValues = {
  destroy: () => void;
  update: () => void;
  initialize: () => void;
  isInitialized: boolean;
};

const StickySidebarContext = createContext<StickySidebarContextValues>({
  destroy: () => undefined,
  update: () => undefined,
  initialize: () => undefined,
  isInitialized: false,
});

type StickySidebarProviderProps = {
  /** id of the container div, default = 'zola-shop' */
  id?: string;

  /** Optional classname to apply to the container */
  className?: string;

  /** CSS selector for the sticky sidebar, default: .zola-shop-sidebar */
  sidebarSelector?: string;

  /**
   * Additional top spacing of the element when it becomes sticky.
   */
  topSpacing?: 0;

  /**
   * Additional bottom spacing of the element when it becomes sticky.  Default = 0;
   */
  bottomSpacing?: number;

  /**
   * Container sidebar selector to know what the beginning and end of sticky element.
   */
  containerSelector?: boolean;

  /**
   * Inner wrapper selector.
   */
  innerWrapperSelector?: string;

  /**
   * Detect when sidebar and its container change height so re-calculate their dimensions.
   */
  resizeSensor?: boolean;
};

export const useStickySidebar = () => useContext(StickySidebarContext);

export const StickySidebarProvider: React.FC<StickySidebarProviderProps> = ({
  children,
  id = 'zola-shop',
  sidebarSelector = '.zola-shop-sidebar',
  className,
  topSpacing = FILTER_POSITION_SETTINGS.top,
  bottomSpacing = 0,
  containerSelector = '.shop-body-container',
  innerWrapperSelector = '.sidebar__inner',
  resizeSensor = true,
}) => {
  const [stickySidebar, setStickySidebar] = useState<{
    destroy: () => void;
    updateSticky: () => void;
  } | null>(null);

  const destroy = useCallback(() => {
    if (stickySidebar) {
      stickySidebar.destroy();
      setStickySidebar(null);
    }
  }, [stickySidebar]);

  const update = useCallback(() => {
    if (stickySidebar) {
      stickySidebar.updateSticky();
    }
  }, [stickySidebar]);

  const StickySidebar = useRef<any>();
  useEffect(() => {
    if (!StickySidebar.current) {
      const dynamicallyLoadStickySidebarBecauseItUsesWindow = async () => {
        // @ts-expect-error types are not defined for 'sticky-sidebar/dist/sticky-sidebar'
        StickySidebar.current = await import('sticky-sidebar/dist/sticky-sidebar');
      };
      dynamicallyLoadStickySidebarBecauseItUsesWindow().catch(() => null);
    }
  });
  const initialize = useCallback(() => {
    if (!StickySidebar.current) {
      return;
    }
    const Constructor = StickySidebar.current.default;

    setStickySidebar(
      new Constructor(sidebarSelector, {
        topSpacing,
        bottomSpacing,
        containerSelector,
        innerWrapperSelector,
        resizeSensor,
      })
    );
  }, [
    bottomSpacing,
    containerSelector,
    innerWrapperSelector,
    resizeSensor,
    sidebarSelector,
    topSpacing,
  ]);

  return (
    <StickySidebarContext.Provider
      value={{
        destroy,
        update,
        initialize,
        isInitialized: Boolean(stickySidebar),
      }}
    >
      <StickyContainer id={id} className={className}>
        {children}
      </StickyContainer>
    </StickySidebarContext.Provider>
  );
};
